import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { SEOHead } from '../components/seo';
import { useStats } from '../hooks/useHomepageQueries';
import { getImageUrl } from '../lib/utils';

/* ─── Animated counter hook ─── */
const useCounter = (target, duration = 1600) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const step = target / (duration / 16);
          const timer = setInterval(() => {
            start += step;
            if (start >= target) { setCount(target); clearInterval(timer); }
            else setCount(Math.floor(start));
          }, 16);
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return [count, ref];
};

/* ─── Stat Item ─── */
const StatItem = ({ value, suffix, label }) => {
  const [count, ref] = useCounter(value);
  return (
    <div ref={ref} className="text-center px-4 py-3">
      <div className="text-3xl font-extrabold text-[#C8102E] mb-0.5">{count}{suffix}</div>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</div>
    </div>
  );
};

/* ─── Category Card ─── */
const CategoryCard = ({ icon, title, description }) => (
  <div className="group bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
    <div className="mb-3 text-[#C8102E]">{icon}</div>
    <h3 className="text-sm font-bold text-gray-900 mb-1">{title}</h3>
    <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
  </div>
);

/* ─── Benefit Item ─── */
const BenefitItem = ({ icon, title, description }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 text-[#C8102E] mt-0.5">{icon}</div>
    <div>
      <h4 className="text-sm font-bold text-gray-900 mb-0.5">{title}</h4>
      <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
    </div>
  </div>
);

/* ─── SVG Icons ─── */
const IconSurgical = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 9.5l-5 5" /><circle cx="7" cy="17" r="2" /><path d="M2 22l3-3" /><path d="M13 2l9 9-3.5 3.5a4.95 4.95 0 01-7-7L13 2z" />
  </svg>
);
const IconDental = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5.5C10 2 6 2 5 5c-1 2.5 0 5 1 7 .8 1.6 1 3.5 1 4.5 0 1.5.5 2.5 1.5 2.5s1.5-1 1.5-2.5V14h2v1.5c0 1.5.5 2.5 1.5 2.5s1.5-1 1.5-2.5c0-1 .2-2.9 1-4.5 1-2 2-4.5 1-7-1-3-5-3-7 .5z" />
  </svg>
);
const IconSkincare = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" /><path d="M12 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /><path d="M8 16c.8-1.2 2.2-2 3.9-2h.2c1.7 0 3.1.8 3.9 2" />
  </svg>
);
const IconHealth = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);
const IconBrand = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
  </svg>
);
const IconQuality = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 12l2 2 4-4" /><path d="M21 12c0 1.2-.504 2.3-1.35 3.1L12 22l-7.65-6.9A4.3 4.3 0 013 12V5.4c0-.9.6-1.7 1.5-2L12 1l7.5 2.4c.9.3 1.5 1.1 1.5 2V12z" />
  </svg>
);
const IconShopping = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 001.94 1.51h9.72a2 2 0 001.94-1.51L23 6H6" />
  </svg>
);
const IconSecure = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);
const IconDelivery = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);
const IconSupport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);

/* ─── Brand Logo Tile ─── */
const BrandTile = ({ brand }) => {
  const [imgError, setImgError] = useState(false);
  return (
    <div className="flex items-center justify-center h-14 bg-white border border-gray-100 rounded-lg px-3 shadow-sm hover:border-red-200 hover:shadow-md transition-all duration-300">
      {brand.picture && !imgError ? (
        <img
          src={getImageUrl(brand.picture)}
          alt={brand.name}
          className="max-h-7 max-w-full object-contain transition-all duration-300"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="text-xs font-bold text-gray-400 text-center">{brand.name}</span>
      )}
    </div>
  );
};

/* ─── Section Header ─── */
const SectionHeader = ({ label, title, subtitle }) => (
  <div className="mb-8">
    <div className="text-xs font-bold text-[#C8102E] uppercase tracking-widest mb-2">{label}</div>
    <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500 max-w-xl leading-relaxed">{subtitle}</p>}
  </div>
);

/* ─── Main Page ─── */
const AboutPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { data: brandsData } = useQuery({
    queryKey: ['about-brands'],
    queryFn: async () => {
      const res = await api.get('/brands', { params: { status: 'active', featured: 'true', limit: 12 } });
      if (res.data?.success && res.data?.data?.brands) return res.data.data.brands;
      if (res.data?.data) return Array.isArray(res.data.data) ? res.data.data : [];
      return [];
    },
    staleTime: 10 * 60 * 1000,
  });

  const { data: stats } = useStats();

  const brands = brandsData || [];

  return (
    <>
      <SEOHead
        title="About CureHive Nepal - Trusted Healthcare & Wellness Marketplace"
        description="CureHive Nepal is an online marketplace for surgical, dental, skincare, and medical products."
        type="website"
      />

      <main style={{ fontFamily: "'Nunito', sans-serif" }} className="bg-white text-gray-800">

        {/* ── 1. HERO ─────────────────────────────────────── */}
        <section className="bg-[#FAFAFA] border-b border-gray-100 py-10 md:py-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center bg-red-50 text-[#C8102E] text-xs font-bold px-3 py-1 rounded-full mb-4 border border-red-100">
                  Nepal's Healthcare Marketplace
                </div>
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
                  Better Care.<br />
                  Trusted Products.<br />
                  <span className="text-[#C8102E]">One Place.</span>
                </h1>
                <p className="text-sm md:text-base text-gray-500 leading-relaxed mb-6 max-w-md">
                  CureHive Nepal brings trusted healthcare, surgical, dental, skincare, and wellness products together in one convenient online marketplace.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#a50d26] text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-all duration-200 shadow-sm"
                  >
                    Explore Products
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </Link>
                  <a
                    href="#who-we-are"
                    className="inline-flex items-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-700 text-sm font-bold px-5 py-2.5 rounded-lg transition-all duration-200 hover:bg-gray-50"
                  >
                    Learn More
                  </a>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="w-full max-w-lg">
                  <img
                    src="/images/curehive2.png"
                    alt="CureHive Nepal healthcare and wellness products"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 2. WHO WE ARE ────────────────────────────────── */}
        <section id="who-we-are" className="py-10 md:py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center gap-0 bg-white rounded-2xl shadow-[0_2px_24px_rgba(0,0,0,0.07)] border border-gray-100 overflow-hidden">

              {/* ── Left: Image panel ── */}
              <div className="w-full md:w-[45%] bg-[#F3F4F6] flex items-center justify-center min-h-[260px] md:min-h-[380px] p-6 md:p-8">
                <img
                  src="/images/curehive1.png"
                  alt="CureHive Nepal dental and medical equipment range"
                  className="w-full max-w-[420px] h-auto object-contain"
                />
              </div>

              {/* ── Right: Text panel ── */}
              <div className="w-full md:w-[55%] px-8 md:px-12 py-10 md:py-12 flex flex-col justify-center">
                {/* Label */}
                <div className="text-xs font-bold text-[#C8102E] uppercase tracking-[0.18em] mb-3">
                  Our Story
                </div>

                {/* Heading */}
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-5 leading-tight">
                  Who We Are
                </h2>

                {/* Body paragraphs */}
                <div className="space-y-3 mb-8">
                  <p className="text-sm md:text-base text-gray-500 leading-relaxed">
                    CureHive Nepal is an online healthcare and wellness marketplace created to make quality products easier to discover and purchase across Nepal.
                  </p>
                  <p className="text-sm md:text-base text-gray-500 leading-relaxed">
                    From surgical and medical essentials to dental care and skincare products, we bring products from trusted brands together in one convenient platform.
                  </p>
                  <p className="text-sm md:text-base text-gray-500 leading-relaxed">
                    Our goal is to make healthcare shopping simpler, reliable, and accessible.
                  </p>
                </div>

                {/* CTA Button — pill style with arrow circle */}
                <Link
                  to="/products"
                  className="inline-flex items-center gap-3 self-start group"
                >
                  <span className="w-9 h-9 rounded-full border-2 border-gray-800 flex items-center justify-center group-hover:bg-gray-900 group-hover:border-gray-900 transition-all duration-200">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-800 group-hover:text-white transition-colors duration-200">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                  <span className="text-sm font-bold text-gray-800 group-hover:text-gray-600 transition-colors duration-200">
                    Discover CureHive
                  </span>
                </Link>
              </div>

            </div>
          </div>
        </section>

        {/* ── 3. WHAT WE OFFER ─────────────────────────────── */}
        <section className="bg-[#FAFAFA] py-10 md:py-14 border-y border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionHeader
              label="Our Product Range"
              title="What We Offer"
              subtitle="A curated selection of healthcare and wellness products across multiple specialties, sourced from trusted brands."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <CategoryCard icon={<IconSurgical />} title="Surgical & Medical" description="Essential medical and surgical products for healthcare and professional needs." />
              <CategoryCard icon={<IconDental />} title="Dental Care" description="Quality dental products and supplies for professionals, clinics, and personal care." />
              <CategoryCard icon={<IconSkincare />} title="Skincare & Personal Care" description="Skincare and personal care products from recognized brands." />
              <CategoryCard icon={<IconHealth />} title="Healthcare Essentials" description="Everyday healthcare and wellness products conveniently available online." />
            </div>
          </div>
        </section>

        {/* ── 4. WHY CHOOSE US ─────────────────────────────── */}
        <section id="why-choose-us" className="py-10 md:py-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div>
                <SectionHeader
                  label="Our Commitment"
                  title="Why Choose CureHive Nepal?"
                  subtitle="We are committed to offering a reliable and professional healthcare shopping experience."
                />
                <div className="w-8 h-0.5 bg-[#C8102E] rounded mb-5" />
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#a50d26] text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-all duration-200 shadow-sm"
                >
                  Browse Products
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <BenefitItem icon={<IconBrand />} title="Trusted Brands" description="We carry products from brands with recognized quality and professional standards." />
                <BenefitItem icon={<IconQuality />} title="Quality-Focused" description="Every product goes through careful selection to meet healthcare expectations." />
                <BenefitItem icon={<IconShopping />} title="Easy Online Shopping" description="A clean, intuitive platform that makes finding the right product simple." />
                <BenefitItem icon={<IconSecure />} title="Secure Payments" description="Checkout with confidence using secure and trusted payment methods." />
                <BenefitItem icon={<IconDelivery />} title="Reliable Delivery" description="Orders delivered across Nepal with care and reasonable timeframes." />
                <BenefitItem icon={<IconSupport />} title="Customer Support" description="Friendly support available to help with orders, products, and questions." />
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. MISSION & VISION ──────────────────────────── */}
        <section className="bg-[#FAFAFA] border-y border-gray-100 py-10 md:py-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionHeader label="Our Purpose" title="Mission & Vision" />
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-white border-l-4 border-[#C8102E] rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C8102E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                  <span className="text-xs font-bold text-[#C8102E] uppercase tracking-widest">Our Mission</span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed font-semibold">
                  "To make quality healthcare and wellness products more accessible, convenient, and reliable for customers across Nepal."
                </p>
              </div>
              <div className="bg-white border-l-4 border-[#FCC419] rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e6a800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                  <span className="text-xs font-bold text-[#b38200] uppercase tracking-widest">Our Vision</span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed font-semibold">
                  "To become one of Nepal's most trusted online destinations for healthcare, medical, dental, and skincare products."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 6. TRUSTED BRANDS ────────────────────────────── */}
        <section className="py-10 md:py-14 border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionHeader
              label="Brand Partners"
              title="Products From Brands You Trust"
              subtitle="We work with recognized healthcare brands to bring you quality and reliability in every product."
            />
            {brands.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {brands.map(brand => <BrandTile key={brand._id || brand.id} brand={brand} />)}
              </div>
            ) : (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-5 py-4 max-w-xl">
                <svg width="16" height="16" className="text-amber-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                <p className="text-xs text-amber-700 leading-relaxed">
                  <strong>Admin tip:</strong> Go to{' '}
                  <Link to="/admin/brands" className="underline font-bold">Admin Panel → Brands</Link>,
                  upload a logo, and toggle <em>Featured</em> to show brands here.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ── 7. STATISTICS STRIP ──────────────────────────── */}
        <section className="bg-[#FAFAFA] py-6 border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 md:divide-x divide-gray-200">
              <StatItem value={stats?.productsCount || 0} suffix="+" label="Products" />
              <StatItem value={stats?.brandsCount || 0} suffix="+" label="Trusted Brands" />
              <StatItem value={stats?.categoriesCount || 0} suffix="+" label="Categories" />
              <div className="text-center px-4 py-3">
                <div className="text-3xl font-extrabold text-[#C8102E] mb-0.5">NP</div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Nationwide</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 8. FINAL CTA ─────────────────────────────────── */}
        <section className="py-10 md:py-14 bg-[#C8102E]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-snug">
              Better Care Starts With Better Choices.
            </h2>
            <p className="text-red-100 text-sm mb-6 leading-relaxed">
              Discover healthcare and wellness products from trusted brands, all in one place.
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-white text-[#C8102E] hover:bg-gray-50 text-sm font-bold px-7 py-3 rounded-lg transition-all duration-200 shadow-lg"
            >
              Shop Now
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </Link>
          </div>
        </section>

      </main>
    </>
  );
};

export default AboutPage;
